import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Card, Text, Button } from '@nextiva/next-ui';
import { useFeature } from '@nextiva/feature-toggle';
import {
  billingLandingRoutes,
  PaymentMethodDetails,
  InvoiceInfo,
} from 'components';
import { INVOICE_STATUS, MAKE_PAYMENT, MOONSHOT_OUTAGE_FLAG } from 'constant';
import { formatAmount } from 'formatters';
import { parseToInternalStructure } from 'parsers/paymentParser/paymentParser';

import './Summary.scss';

export const blockName = 'summary';

const { PAYMENT, INVOICES } = billingLandingRoutes;

const Summary = props => {
  const {
    togglePaymentDrawer,
    nextInvoiceDate,
    contactInfo: {
      account_manager: {
        contact: { first_name, last_name },
      },
    },
    plan: {
      offeringName,
      billingFrequency,
      contractTerm,
      licenses,
      totalMonthlyBill,
    },
    bill: {
      endDate,
      licenses: billLicenses,
      paymentDueDate,
      service,
      startDate,
      taxes,
      totalMonthlyBill: totaBill,
      usageOverage,
      hasOverage,
      invoiceStatus,
      isPaymentSupported,
    },
    invoices,
    defaultPaymentMethod,
    setPaymentDisabled,
    setPaymentMethodCapabilityEnabled,
    isPaymentDisabled,
    isPaymentMethodCapabilityEnabled,
  } = props;

  const { isEnabled: moonshotOutage } = useFeature({
    name: MOONSHOT_OUTAGE_FLAG,
  });

  const primaryContact = `${first_name} ${last_name}`;

  useEffect(() => {
    setPaymentDisabled(!hasOverage && INVOICE_STATUS.CLOSED === invoiceStatus);
  }, [hasOverage, invoiceStatus, setPaymentDisabled]);
  useEffect(() => {
    setPaymentMethodCapabilityEnabled(!!isPaymentSupported);
  }, [isPaymentSupported, setPaymentMethodCapabilityEnabled]);

  return (
    <div data-testid={blockName} className={blockName}>
      <Card className={`${blockName}__card ${blockName}--small-margin-bottom`}>
        <div className={`${blockName}__card-title`}>
          <div>
            <Text className={`${blockName}--grey-text`}>Your current bill</Text>
            <Text kind="h2">
              {startDate} - {endDate}
            </Text>
          </div>
          {!moonshotOutage && isPaymentMethodCapabilityEnabled && (
            <Button
              kind="primary"
              className={`${blockName}__payment-btn`}
              onClick={togglePaymentDrawer}
              disabled={isPaymentDisabled}
            >
              {MAKE_PAYMENT}
            </Button>
          )}
        </div>
        <div
          className={`${blockName}__row-with-value ${blockName}--small-margin-bottom`}
        >
          <Text>
            <span className={`${blockName}__nx-text--bold`}>
              Primary Contact:{' '}
            </span>
            <span
              className={`${blockName}--grey-text ${blockName}--font-weight-medium`}
            >
              {primaryContact}
            </span>
            <a href="/apps/platform-admin/#/accountinfo">
              <span className={`${blockName}__invoice-info`}> (change)</span>
            </a>
          </Text>
        </div>
        <div
          className={`${blockName}__info ${blockName}--medium-margin-bottom`}
        >
          <Text kind="h4" className={`${blockName}--small-margin-bottom`}>
            Invoice Date:{' '}
            <span
              className={`${blockName}--grey-text`}
              id={`${blockName}-invoice-date`}
            >
              {paymentDueDate}
            </span>
          </Text>
          <div
            className={`${blockName}__row-with-value ${blockName}--small-margin-bottom`}
          >
            <Text kind="h4">
              Licenses:{' '}
              <span
                className={`${blockName}--grey-text ${blockName}--font-weight-medium`}
              >
                {billLicenses}
              </span>
            </Text>
            <Text kind="h4">{formatAmount(service)}</Text>
          </div>
          <div
            className={`${blockName}__row-with-value ${blockName}--small-margin-bottom`}
          >
            <Text kind="h4">Usage Overage</Text>
            <Text kind="h4">{formatAmount(usageOverage)}</Text>
          </div>
          <div className={`${blockName}__row-with-value`}>
            <Text kind="h4">Taxes, Fees, Surcharges</Text>
            <Text kind="h4">{formatAmount(taxes)}</Text>
          </div>
        </div>
        <div className={`${blockName}__card-total`}>
          <p
            className={`${blockName}--font-size-medium ${blockName}--font-weight-medium`}
          >
            Total this month
          </p>
          <Text kind="h4">{formatAmount(totaBill)}</Text>
        </div>
      </Card>

      <div className={`${blockName}__middle-cards`}>
        <Card
          className={`${blockName}__card ${blockName}--margin-right ${blockName}--small-flex-grow`}
        >
          <div className={`${blockName}__middle-card-title`}>
            <Text kind="h4">Payment Details</Text>
            {!moonshotOutage && isPaymentMethodCapabilityEnabled && (
              <Link to={PAYMENT.path}>
                <span className={`${blockName}__link-to-tab`}>Manage</span>
              </Link>
            )}
          </div>
          <div className={`${blockName}__payment-method`}>
            <PaymentMethodDetails
              isPaymentMethodCapabilityEnabled={
                isPaymentMethodCapabilityEnabled
              }
              paymentMethod={parseToInternalStructure(defaultPaymentMethod)}
            />
          </div>
          <div className={`${blockName}__next-invoice`}>
            Next invoice is on {nextInvoiceDate}
          </div>
        </Card>
        <Card className={`${blockName}__card`}>
          <div className={`${blockName}__middle-card-title`}>
            <Text kind="h4">Invoices</Text>
            <Link to={INVOICES.path}>
              <span className={`${blockName}__link-to-tab`}>See all</span>
            </Link>
          </div>
          <div className={`${blockName}__invoice-info`}>
            {invoices.map(invoice => (
              <InvoiceInfo invoice={invoice} key={invoice.eventId} />
            ))}
          </div>
        </Card>
      </div>

      <Card className={`${blockName}__card`}>
        <div className={`${blockName}__card-title`}>
          <div>
            <Text className={`${blockName}--grey-text`}>Your Nextiva Plan</Text>
            <Text kind="h2">{offeringName}</Text>
          </div>
        </div>
        <div className={`${blockName}__info`}>
          <Text kind="h4" className={`${blockName}--small-margin-bottom`}>
            Billed:{' '}
            <span className={`${blockName}--grey-text`}>
              {billingFrequency}
            </span>
          </Text>
          <Text kind="h4" className={`${blockName}--small-margin-bottom`}>
            Contract Term:{' '}
            <span
              className={`${blockName}--grey-text`}
              id={`${blockName}-contract-term`}
            >
              {contractTerm}
            </span>
          </Text>
          <Text kind="h4" className={`${blockName}--small-margin-bottom`}>
            Licenses:{' '}
            <span
              className={`${blockName}--grey-text ${blockName}--font-weight-medium`}
            >
              {licenses}
            </span>
          </Text>
        </div>
        <div className={`${blockName}__card-total ${blockName}--card-hidden`}>
          <p
            className={`${blockName}--font-size-medium ${blockName}--font-weight-medium`}
          >
            Estimated total monthly bill
          </p>
          <Text kind="h4">{formatAmount(totalMonthlyBill)}</Text>
        </div>
      </Card>
    </div>
  );
};

Summary.propTypes = {
  plan: PropTypes.object.isRequired,
  contactInfo: PropTypes.object.isRequired,
  bill: PropTypes.object.isRequired,
  defaultPaymentMethod: PropTypes.object.isRequired,
  nextInvoiceDate: PropTypes.string.isRequired,
  invoices: PropTypes.array.isRequired,
  togglePaymentDrawer: PropTypes.func.isRequired,
  setPaymentDisabled: PropTypes.func.isRequired,
  setPaymentMethodCapabilityEnabled: PropTypes.func.isRequired,
  isPaymentDisabled: PropTypes.bool.isRequired,
  isPaymentMethodCapabilityEnabled: PropTypes.bool.isRequired,
};

export default Summary;
